import { copyText } from '../../core/copy';
import { clear, el } from '../../core/dom';
import { makeBanner, makeChip, makeFieldHead, makePanel, setChip, type StatusTone } from '../../core/shell';
import { analyzeUrl, type UrlAnalysis } from './parse';
import { SAMPLE_URL } from './sample';

function fillPanels(panels: HTMLElement, result: UrlAnalysis, idle: boolean): void {
	clear(panels);
	if (idle || !result.ok || !result.parts) {
		panels.appendChild(
			makePanel(
				'Findings',
				el('p', { className: 'kb-tools__muted' }, [
					'Paste a URL to inspect host, encoding, and common phishing-shaped structures — without opening the link.',
				])
			)
		);
		return;
	}

	const parts = el('dl', { className: 'kb-tools__claim-list' });
	const rows: Array<[string, string]> = [
		['Scheme', result.parts.scheme],
		['Userinfo', result.parts.userinfo || '—'],
		['Host', result.parts.host],
		['Unicode host', result.parts.hostUnicode],
		['eTLD+1', result.parts.etld1],
		['Port', result.parts.port],
		['Path', result.parts.path],
		['Query', result.parts.query || '—'],
		['Fragment', result.parts.hash || '—'],
	];
	for (const [k, v] of rows) {
		parts.appendChild(el('dt', {}, [k]));
		parts.appendChild(el('dd', {}, [v]));
	}
	panels.appendChild(makePanel('Parsed parts', parts));

	const flagsBody = el('div', { className: 'kb-tools__flag-list' });
	if (!result.flags.length) {
		flagsBody.appendChild(
			el('p', { className: 'kb-tools__muted' }, [
				'No indicators from these local checks. That is not a safety guarantee.',
			])
		);
	} else {
		for (const flag of result.flags) {
			const block = el('article', { className: 'kb-tools__flag' });
			block.appendChild(el('h4', { className: 'kb-tools__flag-title' }, [flag.title]));
			block.appendChild(el('p', {}, [flag.detail]));
			block.appendChild(
				el('p', { className: 'kb-tools__muted' }, [`Likely false positives: ${flag.falsePositive}`])
			);
			flagsBody.appendChild(block);
		}
	}
	panels.appendChild(makePanel('Heuristic flags', flagsBody));
}

export function mountUrlAnalysis(root: HTMLElement): void {
	root.classList.add('kb-tools', 'kb-tools--url');
	root.querySelectorAll('.kb-tools__ssr-fallback').forEach((n) => n.remove());

	const input = el('textarea', {
		className: 'kb-tools__textarea',
		id: 'kb-url-input',
		rows: '5',
		spellcheck: 'false',
		autocomplete: 'off',
		'aria-describedby': 'kb-url-status kb-url-privacy',
	}) as HTMLTextAreaElement;
	input.placeholder = 'https://… (will not be opened)';

	const analyzeBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--primary' }, [
		'Analyze',
	]) as HTMLButtonElement;
	const sampleBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Paste sample',
	]) as HTMLButtonElement;
	const clearBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Clear',
	]) as HTMLButtonElement;
	const copyBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Copy findings',
	]) as HTMLButtonElement;

	const compose = el('div', { className: 'kb-tools__compose' }, [
		makeFieldHead(el('label', { className: 'kb-tools__label', for: 'kb-url-input' }, ['URL']), '⌘/Ctrl + Enter'),
		input,
		el('div', { className: 'kb-tools__actions' }, [analyzeBtn, sampleBtn, clearBtn, copyBtn]),
	]);

	const chip = makeChip('kb-url-status');
	const warnings = el('ul', { className: 'kb-tools__warnings' });
	const panels = el('div', { className: 'kb-tools__panels kb-tools__panels--stack' });
	const results = el('div', { className: 'kb-tools__results' }, [
		el('div', { className: 'kb-tools__status-row' }, [chip]),
		warnings,
		panels,
	]);

	let lastReport = '';
	let idle = true;

	clear(root);
	root.append(
		makeBanner('Local', 'No fetch, DNS lookup, screenshot, or reputation check — the URL is parsed in this browser only.'),
		el('div', { className: 'kb-tools__workspace' }, [compose, results]),
		el('p', { className: 'kb-tools__privacy', id: 'kb-url-privacy' }, [
			'Results never say “safe” or “malicious.” Links in findings are not clickable.',
		])
	);

	const paint = (result: UrlAnalysis): void => {
		let tone: StatusTone = 'idle';
		let text = 'Waiting for a URL';
		if (!idle) {
			if (!result.ok) {
				tone = 'error';
				text = 'Unable to analyze';
			} else if (result.verdict === 'indicators') {
				tone = 'warn';
				text = 'Indicators found';
			} else {
				tone = 'ok';
				text = 'No indicators from these checks';
			}
		}
		setChip(chip, text, tone);

		clear(warnings);
		if (!idle && result.inferredScheme) {
			warnings.appendChild(el('li', {}, ['https:// was inferred so the browser could parse the string.']));
		}
		if (!idle && !result.ok && result.message) {
			warnings.appendChild(el('li', {}, [result.message]));
		}
		fillPanels(panels, result, idle);
	};

	const run = (force = false): void => {
		if (force) {
			idle = false;
		} else {
			idle = !input.value.trim();
		}
		const result = analyzeUrl(input.value);
		lastReport = result.report;
		paint(result);
	};

	analyzeBtn.addEventListener('click', () => run(true));
	sampleBtn.addEventListener('click', () => {
		input.value = SAMPLE_URL;
		run(true);
	});
	clearBtn.addEventListener('click', () => {
		input.value = '';
		lastReport = '';
		idle = true;
		paint(analyzeUrl(''));
		input.focus();
	});
	copyBtn.addEventListener('click', () => {
		if (lastReport) {
			void copyText(lastReport, copyBtn);
		}
	});
	input.addEventListener('keydown', (event) => {
		if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
			event.preventDefault();
			run(true);
		}
	});

	paint(analyzeUrl(''));
}
